import { Weddings } from '@bridebook/models/source/models/Weddings';
import { IWeddingCampaign } from '@bridebook/models/source/models/Weddings/Campaigns.types';
import { fetchy, FetchyError } from '@bridebook/web/lib/fetchy';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { serverTimestamp } from 'firebase/firestore';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { mapEmailApiError } from '@bridebook/web/lib/rsvp/utils/map-email-api-error';
import { toMillis, useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { useCaptcha } from '@bridebook/web/lib/utils/hooks/use-captcha';
import { getWeddingProfileId } from '@bridebook/web/lib/weddings/selectors';
import { useTranslation } from 'next-i18next';
import type { ICreateEmailCampaignBody } from '@bridebook/web/lib/api/guest-comms/send-campaign.types';
import { isSendCampaignSuccessResponse } from '@bridebook/web/lib/api/guest-comms/send-campaign.utils';

const CACHE_TIME = toMillis(1, 'hour');

const WEDDING_CAMPAIGNS_ROOT_QUERY_KEY = 'campaigns';

export const useGetWeddingCampaigns = (
  mutationFn?: (campaigns: IWeddingCampaign[]) => IWeddingCampaign[],
) => {
  const weddingId = useSelector(getWeddingProfileId);
  const {
    data: emailCampaigns,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: [WEDDING_CAMPAIGNS_ROOT_QUERY_KEY, weddingId],
    queryFn: async () => {
      const campaigns = await Weddings._.getById(weddingId).Campaigns.query().get();
      return Object.values(campaigns);
    },
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    select: mutationFn,
  });
  return {
    emailCampaigns,
    isLoading,
    isRefetching,
  };
};

export const invalidateWeddingCampaignsCache = (queryClient: QueryClient, weddingId: string) => {
  queryClient.invalidateQueries({ queryKey: [WEDDING_CAMPAIGNS_ROOT_QUERY_KEY, weddingId] });
};

// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const updateWeddingCampaignCache = ({
  queryClient,
  weddingId,
  emailCampaignsData,
}: {
  queryClient: QueryClient;
  weddingId: string;
  emailCampaignsData: Omit<IWeddingCampaign, 'createdAt' | 'updatedAt'>;
}) => {
  const queryKey = [WEDDING_CAMPAIGNS_ROOT_QUERY_KEY, weddingId];
  const campaignsList =
    queryClient.getQueryData<Omit<IWeddingCampaign, 'createdAt' | 'updatedAt'>[]>(queryKey) || [];
  queryClient.setQueryData<Omit<IWeddingCampaign, 'createdAt' | 'updatedAt'>[]>(queryKey, [
    ...campaignsList,
    emailCampaignsData,
  ]);
};

export const useCreateWeddingCampaign = () => {
  const weddingId = useSelector(getWeddingProfileId);
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async (campaignData: Omit<IWeddingCampaign, 'createdAt' | 'updatedAt'>) => {
      await Weddings._.getById(weddingId)
        .Campaigns.getById(campaignData.id)
        .set({ ...campaignData, createdAt: serverTimestamp() });

      invalidateWeddingCampaignsCache(queryClient, weddingId);
    },
  });

  return { createEmailCampaign: mutateAsync, isLoading };
};

export const useCreateCampaign = () => {
  const weddingId = useSelector(getWeddingProfileId);
  const { locale, prefix } = useMarket();
  const queryClient = useQueryClient();
  const { getCaptchaToken } = useCaptcha();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async ({
      message,
      subject,
      displayPasswordInEmail,
      shareWeddingWebsiteLink,
      includeGuestsNames,
      recipients,
    }: ICreateEmailCampaignBody['campaign']) => {
      const captchaToken = await getCaptchaToken('sendEmail');

      try {
        const response = await fetchy().authenticated.post('/api/guest-comms/send-campaign', null, {
          campaign: {
            subject,
            message,
            displayPasswordInEmail,
            shareWeddingWebsiteLink,
            includeGuestsNames,
            recipients,
          },
          market: {
            locale,
            prefix,
          },
          captchaToken,
        });

        if (isSendCampaignSuccessResponse(response)) {
          invalidateWeddingCampaignsCache(queryClient, weddingId);

          return {
            data: response.data,
            success: true,
          };
        }
      } catch (error) {
        if (error instanceof FetchyError) {
          if (typeof error.body === 'object' && 'error' in error.body) {
            dispatch(toggleSnackbar('alert', mapEmailApiError(error.body.error, t)));
          }
        }
      }

      return {
        data: null,
        success: false,
      };
    },
  });

  return { createEmailCampaign: mutateAsync, isLoading };
};
